import { useState, useEffect } from 'react';
import { ChevronRight, Users, Settings, CreditCard, Activity, Zap, BarChart3, Globe, AlertCircle, CheckCircle, Clock, TrendingUp, Database, Server } from 'lucide-react';
import { tenantConfigurationService } from '../../services/tenantConfigurationService';
import { useAuth } from '../../hooks/useAuth';
import TenantOverview from './components/TenantOverview';
import TenantCreation from './components/TenantCreation';
import FeatureToggleMatrix from './components/FeatureToggleMatrix';
import ResourceAllocation from './components/ResourceAllocation';
import BulkOperations from './components/BulkOperations';
import Icon from '../../components/AppIcon';


export default function TenantConfiguration() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    activeSubscriptions: 0,
    totalUsers: 0,
    systemLoad: 0
  });

  useEffect(() => {
    loadTenantData();
  }, []);

  const loadTenantData = async () => {
    try {
      setLoading(true);
      const orgsData = await tenantConfigurationService?.getOrganizations();
      setOrganizations(orgsData);
      
      if (orgsData?.length > 0 && !selectedOrganization) {
        setSelectedOrganization(orgsData?.[0]);
      }

      // Calculate stats
      const totalUsers = orgsData?.reduce((sum, org) => sum + org?.members?.length, 0);
      const activeSubscriptions = orgsData?.filter(org => 
        org?.subscription?.status === 'active'
      )?.length;

      setStats({
        totalOrganizations: orgsData?.length,
        activeSubscriptions,
        totalUsers,
        systemLoad: Math.floor(Math.random() * 30) + 40 // Mock system load
      });
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationSelect = (org) => {
    setSelectedOrganization(org);
  };

  const handleRefresh = () => {
    loadTenantData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Loading tenant configuration...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="flex items-center space-x-3 text-red-600 mb-4">
            <AlertCircle className="h-6 w-6" />
            <h2 className="text-lg font-semibold">Error Loading Data</h2>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'creation', label: 'Create Tenant', icon: Users },
    { id: 'features', label: 'Feature Matrix', icon: Zap },
    { id: 'resources', label: 'Resources', icon: Server },
    { id: 'bulk-ops', label: 'Bulk Operations', icon: Database }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Settings className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Tenant Configuration</h1>
                <p className="text-gray-600">Multi-tenant architecture management and system administration</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleRefresh}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Activity className="h-4 w-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* System Status Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4 text-green-500" />
                <span className="text-sm text-gray-600">System Status: </span>
                <span className="text-sm font-medium text-green-600">Operational</span>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-600">{stats?.totalOrganizations} Organizations</span>
              </div>
              <div className="flex items-center space-x-2">
                <CreditCard className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-gray-600">{stats?.activeSubscriptions} Active Subscriptions</span>
              </div>
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4 text-orange-500" />
                <span className="text-sm text-gray-600">System Load: {stats?.systemLoad}%</span>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Administration</h3>
              <nav className="space-y-2">
                {tabs?.map((tab) => {
                  const Icon = tab?.icon;
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeTab === tab?.id
                          ? 'bg-blue-50 text-blue-700 border border-blue-200' :'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{tab?.label}</span>
                      {activeTab === tab?.id && <ChevronRight className="h-4 w-4 ml-auto" />}
                    </button>
                  );
                })}
              </nav>

              {/* Quick Stats */}
              <div className="mt-8">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Quick Stats</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Total Users</span>
                    <span className="font-medium text-gray-900">{stats?.totalUsers}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Active Plans</span>
                    <span className="font-medium text-gray-900">{stats?.activeSubscriptions}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">System Health</span>
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium text-green-600">Good</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Organization Selector */}
            {organizations?.length > 0 && activeTab !== 'creation' && (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Select Organization</h4>
                <select
                  value={selectedOrganization?.id || ''}
                  onChange={(e) => {
                    const org = organizations?.find(o => o?.id === e?.target?.value);
                    if (org) setSelectedOrganization(org);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {organizations?.map((org) => (
                    <option key={org?.id} value={org?.id}>
                      {org?.name} ({org?.status})
                    </option>
                  ))}
                </select>
                
                {selectedOrganization && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Members</span>
                      <span className="font-medium">{selectedOrganization?.members?.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-gray-600">Status</span>
                      <span className={`font-medium capitalize ${
                        selectedOrganization?.status === 'active' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {selectedOrganization?.status}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {activeTab === 'overview' && (
                <TenantOverview 
                  organizations={organizations}
                  stats={stats}
                  selectedOrganization={selectedOrganization}
                  onOrganizationSelect={handleOrganizationSelect}
                  onRefresh={handleRefresh}
                />
              )}
              
              {activeTab === 'creation' && (
                <TenantCreation 
                  onOrganizationCreated={handleRefresh}
                />
              )}
              
              {activeTab === 'features' && selectedOrganization && (
                <FeatureToggleMatrix 
                  organization={selectedOrganization}
                  onFeatureToggled={handleRefresh}
                />
              )}
              
              {activeTab === 'resources' && selectedOrganization && (
                <ResourceAllocation 
                  organization={selectedOrganization}
                  onUpdate={handleRefresh}
                />
              )}
              
              {activeTab === 'bulk-ops' && (
                <BulkOperations 
                  organizations={organizations}
                  onOperationComplete={handleRefresh}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}